import React, { useState } from "react";

function MembersList({ members, onUpdateMember }) {
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [selected, setSelected] = useState(() => new Set(members.map((m) => m.numericId)));

  const allSelected = members.length > 0 && selected.size === members.length;

  const toggleOne = (numericId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(numericId) ? next.delete(numericId) : next.add(numericId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(members.map((m) => m.numericId)));
  };

  const startEdit = (member) => {
    setEditingId(member.numericId);
    setEditData({
      name: member.name || "",
      email: member.email || "",
      phone: member.phone || "",
      sex: member.sex || "",
      age: member.age || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async (member) => {
    await onUpdateMember(member.numericId, editData);
    setEditingId(null);
    setEditData({});
  };

  const exportCSV = () => {
    const toExport = members.filter((m) => selected.has(m.numericId));
    if (toExport.length === 0) return;
    const headers = ["#", "Navn", "E-post", "Telefon", "Kjønn", "Alder"];
    const rows = toExport.map((m, i) => [
      i + 1,
      m.name || "",
      m.email,
      m.phone || "",
      m.sex || "",
      m.age || "",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `medlemmer-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (members.length === 0) {
    return <div className="alert alert-info">📭 Ingen medlemmer registrert ennå.</div>;
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          👥 Registrerte Medlemmer
          <span className="badge bg-success fs-6 ms-2">
            {members.length} {members.length === 1 ? "medlem" : "medlemmer"}
          </span>
        </h5>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={exportCSV}
          disabled={selected.size === 0}
          title={selected.size === 0 ? "Velg minst ett medlem" : `Last ned ${selected.size} medlem(mer)`}
        >
          ⬇️ Last ned CSV {selected.size < members.length && `(${selected.size})`}
        </button>
      </div>

      <div className="table-responsive">
        <table className="table table-striped table-hover align-middle">
          <thead className="table-dark">
            <tr>
              <th>
                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                  title="Velg alle" style={{ cursor: "pointer" }} />
              </th>
              <th>#</th>
              <th>Navn</th>
              <th>Brukernavn</th>
              <th>E-post</th>
              <th>Telefon</th>
              <th>Kjønn</th>
              <th>Alder</th>
              <th>Registrert</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {members.map((member, index) => {
              const isEditing = editingId === member.numericId;
              const isSelected = selected.has(member.numericId);
              return (
                <tr key={member.id} className={isSelected ? "" : "opacity-50"}>
                  <td>
                    <input type="checkbox" checked={isSelected} onChange={() => toggleOne(member.numericId)}
                      style={{ cursor: "pointer" }} />
                  </td>

                  <td>{index + 1}</td>

                  <td>
                    {isEditing ? (
                      <input className="form-control form-control-sm" value={editData.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
                    ) : (
                      <strong>{member.name || "–"}</strong>
                    )}
                  </td>

                  <td>{member.username}</td>

                  <td>
                    {isEditing ? (
                      <input type="email" className="form-control form-control-sm" value={editData.email}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })} />
                    ) : (
                      <a href={`mailto:${member.email}`} className="text-decoration-none">{member.email}</a>
                    )}
                  </td>

                  <td>
                    {isEditing ? (
                      <input type="tel" className="form-control form-control-sm" value={editData.phone}
                        placeholder="123 45 678"
                        onChange={(e) => setEditData({ ...editData, phone: e.target.value })} />
                    ) : (
                      member.phone || "–"
                    )}
                  </td>

                  <td>
                    {isEditing ? (
                      <select className="form-select form-select-sm" value={editData.sex}
                        onChange={(e) => setEditData({ ...editData, sex: e.target.value })}>
                        <option value="">–</option>
                        <option value="Mann">Mann</option>
                        <option value="Kvinne">Kvinne</option>
                      </select>
                    ) : (
                      member.sex || "–"
                    )}
                  </td>

                  <td>
                    {isEditing ? (
                      <select className="form-select form-select-sm" value={editData.age}
                        onChange={(e) => setEditData({ ...editData, age: e.target.value })}>
                        <option value="">–</option>
                        <option value="7-12">7–12</option>
                        <option value="13-18">13–18</option>
                        <option value="19-30">19–30</option>
                        <option value="30+">30+</option>
                      </select>
                    ) : (
                      member.age || "–"
                    )}
                  </td>

                  <td>
                    <small className="text-muted">
                      {member.registered_at
                        ? new Date(member.registered_at).toLocaleDateString("nb-NO")
                        : "–"}
                    </small>
                  </td>

                  <td>
                    {isEditing ? (
                      <div className="d-flex gap-1">
                        <button className="btn btn-success btn-sm" onClick={() => saveEdit(member)}>Lagre</button>
                        <button className="btn btn-outline-secondary btn-sm" onClick={cancelEdit}>Avbryt</button>
                      </div>
                    ) : (
                      <button className="btn btn-outline-primary btn-sm" onClick={() => startEdit(member)}>
                        Rediger
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default MembersList;
